import React, { useState } from 'react';
import {
    Button,
    Card,
    Descriptions,
    Empty,
    List,
    Popconfirm,
    Segmented,
    Space,
    Spin,
    Tag,
    Typography,
    message,
} from 'antd';
import { CheckOutlined, ClockCircleOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetCharterApplicationsQuery,
    useGetCharterApplicationStatsQuery,
    useGetQualificationConfigQuery,
    useReviewCharterApplicationMutation,
} from '../../../services/nestjs-base-api';
import { ICharterApplication } from '../../../services/nestjs-base-api/types';
import { businessModelLabel, productCategoryLabel } from '../../../utils/qualificationLabels';
import { waitlistReason } from '../../../utils/waitlistReason';

const { Text, Title, Paragraph } = Typography;

// Charter Cohort (SYR-106) — the review screen.
//
// Deliberately a list of cards rather than a table: the spec asks for one
// screen with the full submission visible, not filters, sorting and bulk
// operations. Twenty entries at a time. The open question is rendered in full
// because reading it is the point of the screen.
//
// It lives in the CRM for the same reason the concierge queue does — this is
// sales work, and accepting creates a CRM card that the call, the follow-up and
// the close all hang off.

const BAND_LABELS: Record<string, string> = {
    none: 'None they can reach',
    under_500: 'Under 500',
    '500_2000': '500 to 2,000',
    '2000_10000': '2,000 to 10,000',
    '10000_plus': '10,000 or more',
};

const SEND_LABELS: Record<string, string> = {
    yes: 'Yes',
    yes_with_signoff: 'Yes, with sign-off',
    no: 'No, not in that time',
};

const STATUS_COLORS: Record<string, string> = {
    pending: 'processing',
    accepted: 'success',
    held: 'warning',
    unsupported: 'default',
};

export const CrmCharterTab: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken as string;
    const [messageApi, contextHolder] = message.useMessage();
    const [status, setStatus] = useState<string>('pending');

    const { data: applications, isLoading } = useGetCharterApplicationsQuery(
        { jwtToken, status },
        { skip: !jwtToken },
    );
    const { data: stats } = useGetCharterApplicationStatsQuery({ jwtToken }, { skip: !jwtToken });
    const { data: qualificationConfig } = useGetQualificationConfigQuery();
    const [review, reviewResult] = useReviewCharterApplicationMutation();

    const act = async (
        application: ICharterApplication,
        action: 'accept' | 'hold' | 'unsupported' | 'unreview',
    ) => {
        try {
            const updated = await review({ jwtToken, id: application.id, action }).unwrap();
            if (action === 'accept') {
                messageApi.success(
                    intl.formatMessage(
                        { id: 'admin.charter.accepted' },
                        { brand: application.brandName },
                    ),
                );
                if (updated?.crmCompanyId) {
                    messageApi.info(
                        intl.formatMessage({ id: 'admin.charter.cardCreated' }),
                    );
                }
            } else if (action === 'hold') {
                messageApi.success(intl.formatMessage({ id: 'admin.charter.held' }));
            } else if (action === 'unreview') {
                messageApi.success(intl.formatMessage({ id: 'admin.charter.unreviewed' }));
            } else {
                messageApi.success(intl.formatMessage({ id: 'admin.charter.movedToWaitlist' }));
            }
        } catch {
            messageApi.error(intl.formatMessage({ id: 'admin.charter.actionFailed' }));
        }
    };

    const renderFlags = (application: ICharterApplication) => {
        const flags: React.ReactNode[] = [];
        if (!application.qualificationSupported) {
            const reason = waitlistReason(qualificationConfig, {
                productCategory: application.productCategory,
                businessModel: application.businessModel,
            });
            // The stored verdict is what the rules said on the day. The reason
            // is replayed from the LIVE config, so if the rules have changed
            // since, its own colour tells the truth rather than a hardcoded red.
            flags.push(
                <Tag color={reason?.color ?? 'red'} key="type">
                    {reason
                        ? intl.formatMessage({ id: reason.messageId }, reason.values)
                        : intl.formatMessage({ id: 'admin.charter.flag.unsupportedType' })}
                </Tag>,
            );
        }
        if (application.reachableListBand === 'none') {
            flags.push(
                <Tag color="warning" key="list">
                    {intl.formatMessage({ id: 'admin.charter.flag.noReachableList' })}
                </Tag>,
            );
        }
        if (application.canSendWithin30Days === 'no') {
            flags.push(
                <Tag color="warning" key="send">
                    {intl.formatMessage({ id: 'admin.charter.flag.cannotSend' })}
                </Tag>,
            );
        }
        if (application.isFreeMailDomain) {
            flags.push(
                <Tag color="warning" key="mail">
                    {intl.formatMessage({ id: 'admin.charter.flag.personalEmail' })}
                </Tag>,
            );
        }
        if (application.priorSubmissionCount > 0) {
            flags.push(
                <Tag key="prior">
                    {intl.formatMessage(
                        { id: 'admin.charter.flag.priorSubmissions' },
                        { count: application.priorSubmissionCount },
                    )}
                </Tag>,
            );
        }
        return flags;
    };

    if (!jwtToken) {
        return null;
    }

    return (
        <div>
            {contextHolder}

            <Space style={{ marginBottom: 16 }} wrap>
                <Segmented
                    value={status}
                    onChange={(value) => setStatus(value as string)}
                    options={[
                        {
                            label: `${intl.formatMessage({ id: 'admin.charter.status.pending' })}${stats ? ` (${stats.pending})` : ''}`,
                            value: 'pending',
                        },
                        {
                            label: `${intl.formatMessage({ id: 'admin.charter.status.held' })}${stats ? ` (${stats.held})` : ''}`,
                            value: 'held',
                        },
                        {
                            label: `${intl.formatMessage({ id: 'admin.charter.status.accepted' })}${stats ? ` (${stats.accepted})` : ''}`,
                            value: 'accepted',
                        },
                        {
                            label: `${intl.formatMessage({ id: 'admin.charter.status.unsupported' })}${stats ? ` (${stats.unsupported})` : ''}`,
                            value: 'unsupported',
                        },
                        {
                            label: intl.formatMessage({ id: 'admin.charter.status.all' }),
                            value: 'all',
                        },
                    ]}
                />
            </Space>

            {isLoading ? (
                <div style={{ textAlign: 'center', padding: 48 }}>
                    <Spin />
                </div>
            ) : !applications?.length ? (
                <Empty description={intl.formatMessage({ id: 'admin.charter.empty' })} />
            ) : (
                <List
                    dataSource={applications}
                    pagination={{ pageSize: 20, hideOnSinglePage: true }}
                    renderItem={(application) => (
                        <List.Item style={{ padding: 0, marginBottom: 16, display: 'block' }}>
                            <Card
                                size="small"
                                title={
                                    <Space wrap>
                                        <Title level={5} style={{ margin: 0 }}>
                                            {application.brandName}
                                        </Title>
                                        <Tag color={STATUS_COLORS[application.status]}>
                                            {intl.formatMessage({
                                                id: `admin.charter.status.${application.status}`,
                                            })}
                                        </Tag>
                                        {renderFlags(application)}
                                    </Space>
                                }
                                extra={
                                    <Text type="secondary">
                                        {dayjs(application.submittedAt).format('D MMM YYYY, h:mma')}
                                    </Text>
                                }
                                actions={
                                    application.status !== 'pending'
                                        ? [
                                              // The three review actions are one-way
                                              // otherwise, and a misclick here is not
                                              // recoverable without SQL.
                                              <Popconfirm
                                                  key="unreview"
                                                  title={intl.formatMessage({ id: 'admin.charter.confirm.unreview' })}
                                                  description={intl.formatMessage({
                                                      id: application.crmCompanyId
                                                          ? 'admin.charter.confirm.unreviewDetailCrm'
                                                          : 'admin.charter.confirm.unreviewDetail',
                                                  })}
                                                  onConfirm={() => act(application, 'unreview')}
                                                  okButtonProps={{ loading: reviewResult.isLoading }}
                                              >
                                                  <Button type="link" icon={<UndoOutlined style={{ marginRight: 4 }} />}>
                                                      {intl.formatMessage({ id: 'admin.charter.action.unreview' })}
                                                  </Button>
                                              </Popconfirm>,
                                          ]
                                        : [
                                              <Popconfirm
                                                  key="accept"
                                                  title={intl.formatMessage({ id: 'admin.charter.confirm.accept' })}
                                                  description={intl.formatMessage({ id: 'admin.charter.confirm.acceptDetail' })}
                                                  onConfirm={() => act(application, 'accept')}
                                                  okButtonProps={{ loading: reviewResult.isLoading }}
                                              >
                                                  <Button type="link" icon={<CheckOutlined style={{ marginRight: 4 }} />}>
                                                      {intl.formatMessage({ id: 'admin.charter.action.accept' })}
                                                  </Button>
                                              </Popconfirm>,
                                              <Popconfirm
                                                  key="hold"
                                                  title={intl.formatMessage({ id: 'admin.charter.confirm.hold' })}
                                                  description={intl.formatMessage({ id: 'admin.charter.confirm.holdDetail' })}
                                                  onConfirm={() => act(application, 'hold')}
                                              >
                                                  <Button type="link" icon={<ClockCircleOutlined style={{ marginRight: 4 }} />}>
                                                      {intl.formatMessage({ id: 'admin.charter.action.hold' })}
                                                  </Button>
                                              </Popconfirm>,
                                              <Popconfirm
                                                  key="unsupported"
                                                  title={intl.formatMessage({ id: 'admin.charter.confirm.unsupported' })}
                                                  description={intl.formatMessage({ id: 'admin.charter.confirm.unsupportedDetail' })}
                                                  onConfirm={() => act(application, 'unsupported')}
                                              >
                                                  <Button type="link" danger icon={<StopOutlined style={{ marginRight: 4 }} />}>
                                                      {intl.formatMessage({ id: 'admin.charter.action.unsupported' })}
                                                  </Button>
                                              </Popconfirm>,
                                          ]
                                }
                            >
                                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small" bordered={false}>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.website' })}>
                                        <a href={application.websiteUrl} target="_blank" rel="noopener noreferrer">
                                            {application.websiteUrl}
                                        </a>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.contact' })}>
                                        {application.contactName} · {application.email}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.role' })}>
                                        {application.roleAtBrand}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.businessType' })}>
                                        {[
                                            productCategoryLabel(qualificationConfig, application.productCategory),
                                            businessModelLabel(qualificationConfig, application.businessModel),
                                        ]
                                            .filter(Boolean)
                                            .join(' / ') || '—'}
                                        {application.resolvedTemplateType ? ` · ${application.resolvedTemplateType}` : ''}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.emailPlatform' })}>
                                        {application.emailPlatform || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.bought' })}>
                                        {BAND_LABELS[application.customerCountBand] || application.customerCountBand}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.canEmail' })}>
                                        {BAND_LABELS[application.reachableListBand] || application.reachableListBand}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={intl.formatMessage({ id: 'admin.charter.label.canSend' })}>
                                        {SEND_LABELS[application.canSendWithin30Days] || application.canSendWithin30Days}
                                    </Descriptions.Item>
                                </Descriptions>

                                <div style={{ marginTop: 12 }}>
                                    <Text strong>
                                        {intl.formatMessage({ id: 'admin.charter.label.unanswered' })}
                                    </Text>
                                    {/* Rendered in full and never clipped — this is
                                        the field that qualifies the lead. */}
                                    <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 6, marginBottom: 0 }}>
                                        {application.unansweredQuestion}
                                    </Paragraph>
                                </div>

                                {application.crmCompanyId && (
                                    <div style={{ marginTop: 12 }}>
                                        <Link to={`/admin/crm?company=${application.crmCompanyId}`}>
                                            {intl.formatMessage({ id: 'admin.charter.openCard' })}
                                        </Link>
                                    </div>
                                )}
                            </Card>
                        </List.Item>
                    )}
                />
            )}
        </div>
    );
};

export default CrmCharterTab;
